import {
    Box,
    Chip,
    ListItemButton,
    ListItemIcon,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { ChevronRight, OpenInNew } from "@mui/icons-material";
import { formatDateTime, offset } from "../../../utils/formatTime";

import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import { FRONTEND_ROUTES } from "../../../frontendRoutes";
import GrainIcon from "@mui/icons-material/Grain";
import { Link } from "react-router-dom";
import { MODEL_PROPTYPES } from "../../../models";
import PropTypes from "prop-types";
import { getPipelineStatus } from "../../utils";
import { hasPointCloud } from "./utils";

export const PipelineInstanceItem = ({
    isValidated,
    isCtrlClickable,
    pipelineInstance: {
        uuid: pipelineInstanceUuid,
        createdAt,
        PipelineTraitGroup,
        status,
        traitGroupDataTypes,
        translatedPipeline,
    },
    pipeline,
    asLink = true,
}) => {
    const comment = PipelineTraitGroup?.comment;
    const pointCloud = hasPointCloud({ translatedPipeline });

    const handleCtrlClick = (event) => {
        if (event.ctrlKey) {
            event.stopPropagation();
            window.open(
                `/${FRONTEND_ROUTES.TRAFFIC}/${FRONTEND_ROUTES.PIPELINE_INSTANCE}/${pipelineInstanceUuid}`,
                "_blank"
            );
        }
    };

    const { Icon, title, color } = getPipelineStatus(status);

    const displayContent = (
        <>
            {asLink && (
                <ListItemIcon>
                    <Tooltip title={title} placement="left">
                        {Icon ? <Icon color={color} /> : <>&#x2300;</>}
                    </Tooltip>
                </ListItemIcon>
            )}
            <Stack
                direction="row"
                justifyContent="space-between"
                spacing={2}
                width={1}
            >
                <Stack direction="column" spacing={-0.5} flexWrap="wrap">
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography sx={{ fontFamily: "consolas" }}>
                            <AccountTreeIcon
                                sx={{ mr: 0.5, fontSize: "small" }}
                            />
                            {pipelineInstanceUuid.slice(0, 8)}
                        </Typography>
                        {isValidated && (
                            <Tooltip title="Validated">
                                <AssignmentTurnedInIcon
                                    color="success"
                                    fontSize="small"
                                />
                            </Tooltip>
                        )}
                        {isCtrlClickable && (
                            <Box
                                component="span"
                                sx={{
                                    fontSize: "x-small",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    opacity: 0.7,
                                    ml: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        outline: "1px solid darkgray",
                                        borderRadius: "3px",
                                        height: "fit-content",
                                        px: 0.1,
                                    }}
                                    component="kbd"
                                >
                                    ctrl
                                </Box>
                                <OpenInNew
                                    sx={{
                                        fontSize: "small",
                                        color: "text.secondary",
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                    <Box component="span" display="flex" gap={1}>
                        {createdAt && (
                            <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                            >
                                {formatDateTime(
                                    offset(new Date(createdAt), true)
                                )}
                            </Typography>
                        )}
                        <Typography component="span" variant="caption">
                            {status}
                        </Typography>
                    </Box>
                    {comment && (
                        <Tooltip title={comment}>
                            <Typography
                                variant="caption"
                                fontStyle="italic"
                                color="text.secondary"
                                noWrap
                                sx={{ maxWidth: 300 }}
                            >
                                {comment}
                            </Typography>
                        </Tooltip>
                    )}
                </Stack>

                {traitGroupDataTypes ? (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Tooltip
                            title={traitGroupDataTypes
                                .map(({ TraitGroup: { name } }) => name)
                                .join(" | ")}
                        >
                            <Stack direction="row" spacing={-1}>
                                {traitGroupDataTypes
                                    .slice(0, 5)
                                    .map(({ TraitGroup: traitGroup }, index) =>
                                        traitGroup.icon ? (
                                            <Box
                                                key={index}
                                                component="img"
                                                src={traitGroup.icon}
                                                alt={traitGroup.name}
                                                sx={{
                                                    background: "white",
                                                    boxShadow:
                                                        "1px 1px 4px rgba(0,0,0,0.2)",
                                                    borderRadius: "50%",
                                                    height: 24,
                                                    width: 24,
                                                    objectFit: "contain",
                                                }}
                                            />
                                        ) : (
                                            <Chip
                                                key={index}
                                                label={traitGroup.name}
                                                size="small"
                                            />
                                        )
                                    )}
                                {traitGroupDataTypes.length > 5 && (
                                    <Chip
                                        label={`+${
                                            traitGroupDataTypes.length - 5
                                        }`}
                                        size="small"
                                    />
                                )}
                            </Stack>
                        </Tooltip>
                        {pointCloud && (
                            <Tooltip title="Has point cloud">
                                <GrainIcon
                                    color="primary"
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: "50%",
                                        p: 0.3,
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Stack>
                ) : (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {pipeline ? (
                            <Box sx={{ textAlign: "right" }}>
                                <Typography
                                    variant="subtitle2"
                                    color="text.primary"
                                >
                                    {pipeline.mode}
                                </Typography>
                                <Chip
                                    sx={{
                                        fontFamily: "Consolas, monospace",
                                    }}
                                    size="small"
                                    label={pipeline.version}
                                />
                            </Box>
                        ) : (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Pipeline not found
                            </Typography>
                        )}
                        {asLink && <ChevronRight />}
                    </Stack>
                )}
            </Stack>
        </>
    );

    if (!asLink) {
        return <Box onClick={handleCtrlClick}>{displayContent}</Box>;
    }

    return (
        <ListItemButton
            component={Link}
            to={`/${FRONTEND_ROUTES.TRAFFIC}/${FRONTEND_ROUTES.PIPELINE_INSTANCE}/${pipelineInstanceUuid}`}
        >
            {displayContent}
        </ListItemButton>
    );
};

PipelineInstanceItem.propTypes = {
    asLink: PropTypes.bool,
    pipeline: PropTypes.shape(MODEL_PROPTYPES.Pipeline),
    pipelineInstance: PropTypes.shape({
        uuid: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        createdAt: PropTypes.string,
        PipelineTraitGroup: PropTypes.shape({
            comment: PropTypes.string,
        }),
        traitGroupDataTypes: PropTypes.arrayOf(
            PropTypes.shape(MODEL_PROPTYPES.TraitGroupDataType)
        ),
        translatedPipeline: PropTypes.object,
    }).isRequired,
    isValidated: PropTypes.bool,
    isCtrlClickable: PropTypes.bool,
};
